import React, { useMemo, useState } from 'react';
import { PenLine, Type, Upload, Library, MousePointerClick } from 'lucide-react';
import type { SavedSignature } from '../types/fields';
import { SavedSignatureCard } from './SavedSignatureCard';

export interface SignaturesPanelProps {
  savedSignatures: SavedSignature[];
  hasPdf: boolean;
  isBusy?: boolean;
  onCreateDraw: () => void;
  onCreateType: () => void;
  onCreateUpload: () => void;
  onPlace: (signature: SavedSignature) => void;
  onRename: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}

export const SignaturesPanel: React.FC<SignaturesPanelProps> = ({
  savedSignatures,
  hasPdf,
  isBusy = false,
  onCreateDraw,
  onCreateType,
  onCreateUpload,
  onPlace,
  onRename,
  onDelete,
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return savedSignatures;
    return savedSignatures.filter((s) => s.label.toLowerCase().includes(q));
  }, [savedSignatures, search]);

  const createDisabled = isBusy;
  const placeDisabled = !hasPdf || isBusy;

  if (savedSignatures.length === 0) {
    return (
      <div className="signatures-panel signatures-panel-empty">
        <div className="signatures-empty-hero">
          {/* Drop the rounded-square icon topper — the title and copy are
             enough. The icon above the title was filling a template, not
             serving the work. */}
          <h3 className="signatures-empty-title">Your signature library</h3>
          <p className="signatures-empty-desc">
            Create once, reuse on any document. Saved signatures stay on this device only.
          </p>
        </div>

        <div className="signatures-create-grid" role="group" aria-label="Create a signature">
          <button
            type="button"
            className="signatures-create-card"
            disabled={createDisabled}
            onClick={onCreateDraw}
          >
            <PenLine size={18} strokeWidth={1.75} aria-hidden />
            <span className="signatures-create-label">Draw</span>
            <span className="signatures-create-hint">Mouse or stylus</span>
          </button>
          <button
            type="button"
            className="signatures-create-card"
            disabled={createDisabled}
            onClick={onCreateType}
          >
            <Type size={18} strokeWidth={1.75} aria-hidden />
            <span className="signatures-create-label">Type</span>
            <span className="signatures-create-hint">Typed name</span>
          </button>
          <button
            type="button"
            className="signatures-create-card"
            disabled={createDisabled}
            onClick={onCreateUpload}
          >
            <Upload size={18} strokeWidth={1.75} aria-hidden />
            <span className="signatures-create-label">Upload</span>
            <span className="signatures-create-hint">PNG, JPEG, or WebP</span>
          </button>
        </div>

        <ol className="signatures-empty-steps">
          <li>
            <span className="signatures-step-num">1</span>
            Create with Draw, Type, or Upload
          </li>
          <li>
            <span className="signatures-step-num">2</span>
            Choose <strong>Save &amp; place</strong> in the dialog
          </li>
          <li>
            <span className="signatures-step-num">3</span>
            Click the PDF to drop your signature
          </li>
        </ol>

        {!hasPdf ? (
          <p className="signatures-empty-note">
            You can create signatures now. Upload a PDF when you are ready to place them on a document.
          </p>
        ) : (
          <p className="signatures-empty-note signatures-empty-note-ready">
            Pick Draw, Type, or Upload above — then choose Save to library in the dialog.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="signatures-panel">
      <div className="signatures-library-header">
        <div className="signatures-library-title">
          <Library size={14} strokeWidth={2} aria-hidden />
          <span>Saved</span>
          <span className="signatures-library-count">{savedSignatures.length}</span>
        </div>
        <p className="signatures-library-hint">
          <MousePointerClick size={12} aria-hidden />
          Click a card to place on the PDF
        </p>
      </div>

      {savedSignatures.length > 3 && (
        <input
          type="search"
          placeholder="Search signatures…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-signature-search="true"
          className="signatures-search"
          aria-label="Filter saved signatures"
        />
      )}

      <div className="signatures-library-list custom-scrollbar">
        {filtered.map((signature) => (
          <SavedSignatureCard
            key={signature.id}
            signature={signature}
            canPlace={!placeDisabled}
            onPlace={onPlace}
            onRename={onRename}
            onDelete={onDelete}
          />
        ))}
        {filtered.length === 0 && search.trim() && (
          <p className="signatures-no-match">No signatures match “{search}”</p>
        )}
      </div>

      <div className="signatures-quick-add">
        <span className="signatures-quick-add-label">Add another</span>
        <div className="signatures-quick-add-btns">
          <button type="button" disabled={createDisabled} onClick={onCreateDraw}>
            Draw
          </button>
          <button type="button" disabled={createDisabled} onClick={onCreateType}>
            Type
          </button>
          <button type="button" disabled={createDisabled} onClick={onCreateUpload}>
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};